import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { Loading } from '../../../../ui/loading/Loading';
import { ContentEditorModal, ContentEditorValues } from './ContentEditorModal';
import { CommentsAndAttachments } from './CommentsAndAttachments';

type Row = {
    id: number;
    title: string | null;
    story: string | null;
    type: string;
    parent_id?: number | null;
    listOrder: number | null;
    [dateField: string]: unknown;
};

type ModalState = {
    mode: 'create' | 'edit' | 'create-subitem' | 'edit-subitem';
    editing?: Row;
    parentId?: number;
};

type Props = {
    organizationId: string;
    teamId: string;
    initiativeId: string;
    allowEdit: boolean;
    currentUserId?: number;
    sectionLabel: string;
    entityLabel: string;
    entityType: string;
    messageType: string;
    entityParam: 'milestone_id' | 'next_step_id';
    dateField: string;
    dateLabel: string;
    listUrl: string;
    reorderUrl: string;
    itemUrl: (id: number) => string;
    listResponseKey: string;
};

/**
 * Shared accordion for Milestones and Next Steps — structurally identical tables
 * (§0.1 of the migration plan, same note in StrategiesService), each with a top-level
 * "items" tab and a "Discussion" tab (type=Message rows on the same table). One
 * component parameterized by entity, rather than two near-duplicate files.
 *
 * Subitems (added later — see STRATEGIES_MIGRATION_PLAN.md §4c, which deferred this,
 * and the Notion plan's §2.A correction): confirmed live and real by reading
 * `_milestone_subitems.php`/`_next_step_subitems.php` directly — each top-level row
 * can have its own child rows (title/story/date, its own Comments via the same
 * CommentsAndAttachments panel used everywhere else). The backend already supported
 * `parent_id` scoping since Phase 1; only a `show()` endpoint and this UI were
 * missing. Built using the same move-buttons/click-to-expand convention as Details'
 * own subitems — not Yii's numeric-dropdown/always-visible-tabs pattern — per the
 * already-accepted divergence decision (§13.10 of the migration plan).
 * Comments/Links/Uploads on every row (top-level and subitem alike) are wired via the
 * shared CommentsAndAttachments panel (§4d).
 */
export const MilestoneStyleAccordion = ({
    organizationId,
    teamId,
    initiativeId,
    allowEdit,
    currentUserId,
    sectionLabel,
    entityLabel,
    entityType,
    messageType,
    entityParam,
    dateField,
    dateLabel,
    listUrl,
    reorderUrl,
    itemUrl,
    listResponseKey,
}: Props) => {
    // Confirmed live 2026-08-27: MILESTONES/NEXT STEPS are collapsed by default
    // (worksheet_v1.php's `ws-v1-details-section-header` bars), same as DETAILS.
    const [sectionOpen, setSectionOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'items' | 'discussion'>('items');
    const [items, setItems] = useState<Row[]>([]);
    const [messages, setMessages] = useState<Row[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [expandedSubitemId, setExpandedSubitemId] = useState<number | null>(null);
    const [childCache, setChildCache] = useState<Record<number, Row[]>>({});
    const [childLoading, setChildLoading] = useState<number | null>(null);
    const [modal, setModal] = useState<ModalState | null>(null);
    const [saving, setSaving] = useState(false);

    const fetchItems = () => {
        setLoading(true);
        axios
            .get(listUrl)
            .then((response) => setItems(response.data[listResponseKey] ?? []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };

    const fetchMessages = () => {
        setLoading(true);
        axios
            .get(listUrl, { params: { type: messageType } })
            .then((response) => setMessages(response.data[listResponseKey] ?? []))
            .catch(() => setMessages([]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (activeTab === 'items') {
            fetchItems();
        } else {
            fetchMessages();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const rows = activeTab === 'items' ? items : messages;
    const setRows = activeTab === 'items' ? setItems : setMessages;
    const refetch = activeTab === 'items' ? fetchItems : fetchMessages;

    // Subitems only exist on top-level items, not discussion topics (confirmed live
    // — neither `_milestone_subitems.php` nor its Next-Step equivalent is reachable
    // from a Message-type row), so children are only ever fetched for the items tab.
    const fetchChildren = (rowId: number) => {
        setChildLoading(rowId);
        axios
            .get(itemUrl(rowId))
            .then((response) => {
                setChildCache((current) => ({ ...current, [rowId]: response.data.subitems ?? [] }));
            })
            .catch(() => {
                setChildCache((current) => ({ ...current, [rowId]: [] }));
            })
            .finally(() => setChildLoading(null));
    };

    const toggleExpand = (id: number) => {
        if (expandedId === id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(id);
        if (activeTab === 'items' && !childCache[id]) {
            fetchChildren(id);
        }
    };

    const moveRow = (index: number, direction: -1 | 1) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= rows.length) {
            return;
        }
        const reordered = [...rows];
        [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
        setRows(reordered);
        axios.put(reorderUrl, { ordered_ids: reordered.map((row) => row.id) }).catch(() => {
            Swal.fire({ icon: 'error', title: 'Reorder failed', text: 'This could not be reordered.' });
            refetch();
        });
    };

    const moveChild = (parentId: number, index: number, direction: -1 | 1) => {
        const children = childCache[parentId];
        if (!children) {
            return;
        }
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= children.length) {
            return;
        }
        const reordered = [...children];
        [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
        setChildCache((current) => ({ ...current, [parentId]: reordered }));
        axios.put(reorderUrl, { ordered_ids: reordered.map((row) => row.id) }).catch(() => {
            Swal.fire({ icon: 'error', title: 'Reorder failed', text: 'This could not be reordered.' });
            fetchChildren(parentId);
        });
    };

    const removeRow = async (row: Row) => {
        const result = await Swal.fire({
            title: 'Remove this?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove',
        });
        if (!result.isConfirmed) {
            return;
        }
        try {
            await axios.delete(itemUrl(row.id));
            refetch();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Delete failed', text: 'This could not be removed.' });
        }
    };

    const removeChild = async (row: Row, parentId: number) => {
        const result = await Swal.fire({
            title: 'Remove this?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove',
        });
        if (!result.isConfirmed) {
            return;
        }
        try {
            await axios.delete(itemUrl(row.id));
            fetchChildren(parentId);
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Delete failed', text: 'This could not be removed.' });
        }
    };

    const handleModalSubmit = async (values: ContentEditorValues) => {
        setSaving(true);
        try {
            if (modal?.mode === 'edit' && modal.editing) {
                await axios.put(itemUrl(modal.editing.id), {
                    title: values.title,
                    story: values.description || null,
                    [dateField]: values.date || null,
                });
                refetch();
            } else if (modal?.mode === 'edit-subitem' && modal.editing) {
                await axios.put(itemUrl(modal.editing.id), {
                    title: values.title,
                    story: values.description || null,
                    [dateField]: values.date || null,
                });
                if (modal.editing.parent_id) {
                    fetchChildren(modal.editing.parent_id);
                }
            } else if (modal?.mode === 'create-subitem' && modal.parentId) {
                await axios.post(listUrl, {
                    title: values.title,
                    story: values.description || null,
                    [dateField]: values.date || null,
                    parent_id: modal.parentId,
                });
                fetchChildren(modal.parentId);
            } else {
                await axios.post(listUrl, {
                    title: values.title,
                    story: values.description || null,
                    [dateField]: values.date || null,
                    type: activeTab === 'discussion' ? messageType : entityType,
                });
                refetch();
            }
            setModal(null);
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This could not be saved.' });
        } finally {
            setSaving(false);
        }
    };

    const untitledLabel = activeTab === 'discussion' ? 'Untitled topic' : `Untitled ${entityLabel.toLowerCase()}`;
    const emptyLabel = activeTab === 'discussion' ? 'No topics yet.' : `No ${entityLabel.toLowerCase()}s yet.`;

    const sectionModifierClass = entityParam === 'milestone_id' ? 'strategies-section--milestones' : 'strategies-section--next-steps';

    const isSubitemModal = modal?.mode === 'create-subitem' || modal?.mode === 'edit-subitem';

    return (
        <div className={`strategies-details-accordion ${sectionModifierClass} mt-4`}>
            <div
                className="strategies-section-toggle d-flex justify-content-between align-items-center"
                role="button"
                aria-expanded={sectionOpen}
                data-testid="section-toggle"
                onClick={() => setSectionOpen((current) => !current)}
            >
                <h4 className="mb-0">{sectionLabel}</h4>
                <span className="strategies-section-chevron">{sectionOpen ? '▾' : '▸'}</span>
            </div>

            {sectionOpen && (
            <>
            <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
                {/* Confirmed live 2026-08-27 (worksheet_v1.php's `ws-v1-nextsteps-tab-add-btn`/
                `nextsteps-add-message-btn`, same pattern as `_next_steps.php`/`_milestones.php`):
                each tab pill carries its own inline "+" — no separate "+ Add X" button off to
                the side, matching the fix already made for Categories/Discussion. Same
                deliberate simplification as that fix: clicking a tab's "+" also switches to
                that tab, since the create modal's fields are driven by `activeTab`. */}
                <div className="btn-group" role="tablist">
                    <Button
                        as="span"
                        size="sm"
                        variant={activeTab === 'items' ? 'warning' : 'outline-secondary'}
                        onClick={() => setActiveTab('items')}
                        className="d-flex align-items-center gap-2"
                    >
                        {sectionLabel}
                        {allowEdit && (
                            <button
                                type="button"
                                aria-label={`Add ${entityLabel}`}
                                title={`Add ${entityLabel}`}
                                className="strategies-tab-add-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveTab('items');
                                    setModal({ mode: 'create' });
                                }}
                            >
                                +
                            </button>
                        )}
                    </Button>
                    <Button
                        as="span"
                        size="sm"
                        variant={activeTab === 'discussion' ? 'warning' : 'outline-secondary'}
                        onClick={() => setActiveTab('discussion')}
                        className="d-flex align-items-center gap-2"
                    >
                        {activeTab === 'discussion' ? `Discussion (${messages.length})` : 'Discussion'}
                        {allowEdit && (
                            <button
                                type="button"
                                aria-label="Add topic"
                                title="Add topic"
                                className="strategies-tab-add-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveTab('discussion');
                                    setModal({ mode: 'create' });
                                }}
                            >
                                +
                            </button>
                        )}
                    </Button>
                </div>
            </div>

            {loading ? (
                <Loading />
            ) : rows.length === 0 ? (
                <p className="text-muted">{emptyLabel}</p>
            ) : (
                <div className="d-flex flex-column gap-2">
                    {rows.map((row, index) => {
                        const expanded = expandedId === row.id;
                        const dateValue = row[dateField] as string | null | undefined;
                        const children = childCache[row.id];
                        return (
                            <div key={row.id} className="strategies-category-card border rounded p-3">
                                <div className="d-flex justify-content-between align-items-start strategies-card-header-row">
                                    <div className="flex-grow-1" role="button" onClick={() => toggleExpand(row.id)}>
                                        <span className="fw-bold">{row.title || untitledLabel}</span>
                                        {activeTab === 'items' && dateValue && (
                                            <span className="strategies-target-date ms-2">{dateValue}</span>
                                        )}
                                    </div>
                                    {allowEdit && (
                                        <div className="d-flex gap-1">
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={index === 0}
                                                onClick={() => moveRow(index, -1)}
                                                aria-label="Move up"
                                            >
                                                ↑
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                disabled={index === rows.length - 1}
                                                onClick={() => moveRow(index, 1)}
                                                aria-label="Move down"
                                            >
                                                ↓
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => setModal({ mode: 'edit', editing: row })}
                                            >
                                                Edit
                                            </Button>
                                            <Button size="sm" variant="outline-danger" onClick={() => removeRow(row)}>
                                                Remove
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {expanded && (
                                    <div className="mt-2">
                                        {row.story && <div className="text-muted small mb-2">{row.story}</div>}

                                        {activeTab === 'items' && (
                                            <div className="mb-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <span className="ws-v1-section-label small text-muted">Subitems</span>
                                                    {allowEdit && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            onClick={() => setModal({ mode: 'create-subitem', parentId: row.id })}
                                                        >
                                                            + Add Subitem
                                                        </Button>
                                                    )}
                                                </div>
                                                {childLoading === row.id || !children ? (
                                                    <Loading />
                                                ) : children.length === 0 ? (
                                                    <p className="text-muted small">No subitems yet.</p>
                                                ) : (
                                                    <div className="d-flex flex-column gap-1">
                                                        {children.map((sub, subIndex) => {
                                                            const subDateValue = sub[dateField] as string | null | undefined;
                                                            return (
                                                                <div key={sub.id} className="border rounded p-2">
                                                                    <div className="d-flex justify-content-between align-items-start">
                                                                        <div
                                                                            role="button"
                                                                            onClick={() =>
                                                                                setExpandedSubitemId((current) =>
                                                                                    current === sub.id ? null : sub.id
                                                                                )
                                                                            }
                                                                        >
                                                                            <div className="fw-bold small">
                                                                                {sub.title || `Untitled ${entityLabel.toLowerCase()}`}
                                                                                {subDateValue && (
                                                                                    <span className="strategies-target-date ms-2">{subDateValue}</span>
                                                                                )}
                                                                            </div>
                                                                            {sub.story && <div className="text-muted small">{sub.story}</div>}
                                                                        </div>
                                                                        {allowEdit && (
                                                                            <div className="d-flex gap-1">
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="outline-secondary"
                                                                                    disabled={subIndex === 0}
                                                                                    onClick={() => moveChild(row.id, subIndex, -1)}
                                                                                    aria-label="Move subitem up"
                                                                                >
                                                                                    ↑
                                                                                </Button>
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="outline-secondary"
                                                                                    disabled={subIndex === children.length - 1}
                                                                                    onClick={() => moveChild(row.id, subIndex, 1)}
                                                                                    aria-label="Move subitem down"
                                                                                >
                                                                                    ↓
                                                                                </Button>
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="outline-secondary"
                                                                                    aria-label="Edit subitem"
                                                                                    onClick={() => setModal({ mode: 'edit-subitem', editing: sub })}
                                                                                >
                                                                                    Edit
                                                                                </Button>
                                                                                <Button
                                                                                    size="sm"
                                                                                    variant="outline-danger"
                                                                                    aria-label="Remove subitem"
                                                                                    onClick={() => removeChild(sub, row.id)}
                                                                                >
                                                                                    Remove
                                                                                </Button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {expandedSubitemId === sub.id && (
                                                                        <CommentsAndAttachments
                                                                            organizationId={organizationId}
                                                                            teamId={teamId}
                                                                            initiativeId={initiativeId}
                                                                            entityParam={entityParam}
                                                                            entityId={sub.id}
                                                                            allowEdit={allowEdit}
                                                                            currentUserId={currentUserId}
                                                                        />
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <CommentsAndAttachments
                                            organizationId={organizationId}
                                            teamId={teamId}
                                            initiativeId={initiativeId}
                                            entityParam={entityParam}
                                            entityId={row.id}
                                            allowEdit={allowEdit}
                                            currentUserId={currentUserId}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            </>
            )}

            {modal && (
                <ContentEditorModal
                    show
                    title={
                        modal.mode === 'create-subitem'
                            ? `Add ${entityLabel} Subitem`
                            : modal.mode === 'edit-subitem'
                            ? `Edit ${entityLabel} Subitem`
                            : activeTab === 'discussion'
                            ? modal.mode === 'edit'
                                ? 'Edit Topic'
                                : 'New Topic'
                            : modal.mode === 'edit'
                            ? `Edit ${entityLabel}`
                            : `New ${entityLabel}`
                    }
                    titleLabel="Title"
                    // Confirmed live 2026-08-26 (New Milestone/New Next Step) and again
                    // 2026-08-27 by reading worksheet_v1.js/`#milestoneFormModal` directly:
                    // Milestone, Next Step, AND their nested discussion topics all share one
                    // static Yii modal (`openMilestoneFormModal`/`milestoneFormApplyModalTitle`)
                    // whose Title field always reads "Title (required)" with plain "Title"
                    // placeholder, and whose Story field always placeholders "Optional
                    // details" — the same on both tabs, not just the items tab as an earlier
                    // pass had assumed. Applied unconditionally here to match; subitems reuse
                    // the exact same field shape (same table, same columns).
                    titleRequiredSuffix=" (required)"
                    titlePlaceholder="Title"
                    storyPlaceholder="Optional details"
                    saving={saving}
                    showDate={activeTab === 'items' || isSubitemModal}
                    dateLabel={dateLabel}
                    initialValues={
                        modal.editing
                            ? {
                                  title: modal.editing.title ?? '',
                                  description: modal.editing.story ?? '',
                                  date: (modal.editing[dateField] as string | null) ?? '',
                              }
                            : undefined
                    }
                    onHide={() => setModal(null)}
                    onSubmit={handleModalSubmit}
                />
            )}
        </div>
    );
};
